import React, { useState } from 'react';
import BaseReport from './BaseReport';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Pagination,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description'; // Excel icon
import PrintIcon from '@mui/icons-material/Print';
import { format } from 'date-fns';

// Define interface for report data
interface RepaymentRecord {
  id: string;
  loanRef: string;
  borrower: string;
  paymentDueDate: Date;
  principal: number;
  interest: number;
  total: number;
  status: string;
}

// Mock data generation function
const generateMockData = (count: number): RepaymentRecord[] => {
  const data: RepaymentRecord[] = [];
  const startDate = new Date();
  for (let i = 1; i <= count; i++) {
    const dueDate = new Date(startDate);
    dueDate.setDate(startDate.getDate() + i * 7); // Payments due weekly
    const principal = Math.random() * 5000 + 1000;
    const interest = principal * (Math.random() * 0.05 + 0.01);
    data.push({
      id: `PAY-${1000 + i}`,
      loanRef: `LN-${500 + i}`,
      borrower: `Borrower ${String.fromCharCode(65 + (i % 5))}`,
      paymentDueDate: dueDate,
      principal: parseFloat(principal.toFixed(2)),
      interest: parseFloat(interest.toFixed(2)),
      total: parseFloat((principal + interest).toFixed(2)),
      status: i % 3 === 0 ? 'Paid' : 'Pending',
    });
  }
  return data;
};

const DebtRepaymentByPaymentDate: React.FC = () => {
  const [reportData, setReportData] = useState<RepaymentRecord[]>([]);
  const [generatedParams, setGeneratedParams] = useState<Record<string, any> | null>(null);
  const [generationDate, setGenerationDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 5; // Show 5 records per page

  const handleGenerateSampleReport = (filters: Record<string, any>) => {
    console.log('Generating sample report with filters:', filters);
    const mockData = generateMockData(10); // Generate 10 sample records
    setReportData(mockData);
    setGeneratedParams(filters);
    setGenerationDate(new Date());
    setPage(1); // Reset to first page
  };

  const handleExport = (format: 'pdf' | 'excel') => {
    console.log(`Exporting report to ${format} with data:`, currentTableData);
    // Add actual export logic here using libraries like jsPDF, SheetJS etc.
  };

  const handlePrint = () => {
    console.log('Printing report with data:', currentTableData);
    window.print(); // Basic browser print
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Calculate data for the current page
  const currentTableData = reportData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/reports">
          Reports
        </MuiLink>
        <Typography color="text.primary">Debt Repayment by Payment Date</Typography>
      </Breadcrumbs>

      {/* Pass the handler to BaseReport */}
      <BaseReport
        title="Debt Repayment Schedule by Payment Date"
        onGenerateReport={handleGenerateSampleReport}
      >
        {/* Conditional Rendering of the Report Display Area */}
        {reportData.length > 0 && generatedParams && generationDate && (
          <Paper sx={{ mt: 3, p: 3, border: '1px solid #E5E7EB', borderRadius: 2 }}>
            {/* Report Header */}
            <Box sx={{ mb: 2, borderBottom: '1px solid #E5E7EB', pb: 2 }}>
              <Typography variant="h5" gutterBottom>Debt Repayment Schedule by Payment Date</Typography>
              <Typography variant="body2" color="text.secondary">Generated on: {format(generationDate, 'PPpp')}</Typography>
              {/* Display selected parameters - simplified example */}
              <Typography variant="body2" color="text.secondary">
                Parameters: {generatedParams.startDate ? `Start Date: ${format(generatedParams.startDate, 'P')}` : ''}{generatedParams.endDate ? `, End Date: ${format(generatedParams.endDate, 'P')}` : ''}{/* Add more params */}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => handleExport('pdf')}>Export PDF</Button>
              <Button variant="outlined" startIcon={<DescriptionIcon />} onClick={() => handleExport('excel')}>Export Excel</Button>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>Print</Button>
            </Stack>

            {/* Report Content Table */}
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#F9FAFB', fontWeight: 'bold' } }}>
                    <TableCell>Payment ID</TableCell>
                    <TableCell>Loan Ref</TableCell>
                    <TableCell>Borrower</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Principal</TableCell>
                    <TableCell align="right">Interest</TableCell>
                    <TableCell align="right">Total Due</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentTableData.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.loanRef}</TableCell>
                      <TableCell>{row.borrower}</TableCell>
                      <TableCell>{format(row.paymentDueDate, 'P')}</TableCell>
                      <TableCell align="right">{row.principal.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.interest.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.total.toFixed(2)}</TableCell>
                      <TableCell>{row.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Pagination
              count={Math.ceil(reportData.length / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
              color="primary"
            />
          </Paper>
        )}
      </BaseReport>
    </Box>
  );
};

export default DebtRepaymentByPaymentDate; 